import React from 'react';
import PropTypes from 'prop-types';
import Info from '@material-ui/icons/Info';
import WatchLater from '@material-ui/icons/WatchLater';
import { nameOf } from '@cbs-mp/brands-config';

import { get } from '../../../../../util';
import { orCondition } from '../../../../../util/helpers';
import { propTypes as i18nPropTypes } from '../../../../../hocs/intlEquipped';
import { PERIOD_TYPES } from '../../../../../constants/intl';
import statuses, { renewalStatuses, subscriptionStatuses, subscriptionTypes } from '../../../../../constants/statuses';
import { paymentPropType, productPropType, statusPropType } from '../../../../../types/subscription';
import { actionPropType } from '../../../../../types';

import Divider from '../../../../Divider/Divider';

import AdditionalInfo from '../AdditionalInfo';

import EmptyInfoSection from './EmptyInfoSection';

const { EMPTY } = statuses;

const { ACTIVE, INACTIVE, EXPIRED } = subscriptionStatuses;

const { EVERGREEN, TERMED } = subscriptionTypes;

const { CANCELED, DISCONTINUED } = renewalStatuses;

const renderLackNextPaymentInformation =
    ({ formatMessage, formatPeriod }, theme, previewPeriod) => AdditionalInfo({
        text: formatMessage('subscription.renewalSection.header.lackNextPayment', {
            previewPeriod: formatPeriod(previewPeriod),
        }),
        icon: <WatchLater className={ theme.watchLaterIcon } />,
    });

const renderExpireInfoForCancelled =
    ({ i18n: { formatMessage, formatDate }, product, theme }) => AdditionalInfo({
        text: formatMessage('subscription.renewalSection.header.renewalOff.canceled', {
            date: formatDate(product.expires),
        }),
        icon: <Info className={ theme.infoIcon } />,
        isExpireInfo: true,
    });

const InfoSection = ({ status, product, subscriptionId, action, payment, publication, i18n, theme }) => {
    const { formatMessage, formatDate, formatPrice, formatPeriod } = i18n;

    const lacksNextPaymentInformation = payment?.nextBilling?.lackReason === 'TooFarAhead';
    const freeSubscription = payment?.nextBilling?.lackReason === 'Free';
    const price = payment?.recurringBilling?.price || product.price;
    const previewPeriod = payment?.nextBilling?.previewPeriod;
    const isDifferentPayer = payment?.isDifferentPayer;

    const infoSectionRenderers = {
        [ACTIVE]: {
            [EVERGREEN]: {
                [CANCELED]: () => renderExpireInfoForCancelled({ i18n, product, theme }),
                default: () => orCondition(
                    [isDifferentPayer, () => null],
                    [lacksNextPaymentInformation, renderLackNextPaymentInformation(
                        { formatMessage, formatPeriod },
                        theme,
                        previewPeriod
                    )],
                    [freeSubscription, AdditionalInfo({
                        text: formatMessage('subscription.renewalSection.header.evergreen', {
                            price: formatPrice(price),
                        }),
                    })],
                    [true, AdditionalInfo({
                        text: formatMessage('subscription.renewalSection.header.evergreen', {
                            price: formatPrice(price),
                        }),
                    })]
                ),
            },
            [TERMED]: {
                [CANCELED]: () => renderExpireInfoForCancelled({ i18n, product, theme }),
                [DISCONTINUED]: () => AdditionalInfo({
                    text: formatMessage('subscription.renewalSection.header.renewalOff.discontinued', {
                        date: formatDate(product.expires),
                    }),
                    icon: <Info className={ theme.infoIcon } />,
                }),
                default: () => AdditionalInfo({
                    text: formatMessage('subscription.renewalSection.header.renewalOn', {
                        date: formatDate(product.expires),
                        publication: nameOf(publication),
                        price: formatPrice(product.price),
                        renewalPeriod: formatPeriod(product.renewPeriod),
                        renewalPeriodAdverb: formatPeriod(product.renewPeriod, { type: PERIOD_TYPES.ADVERB }),
                    }),
                }),
            },
        },
        [INACTIVE]: {
            [EVERGREEN]: {
                [CANCELED]: () => renderExpireInfoForCancelled({ i18n, product, theme }),
                default: orCondition(
                    [isDifferentPayer, () => null],
                    [true, () => payment?.nextBilling?.lackReason === 'TooFarAhead'
                    && renderLackNextPaymentInformation({ formatMessage, formatPeriod }, theme, previewPeriod)]
                ),
            },
            [TERMED]: {
                [CANCELED]: () => renderExpireInfoForCancelled({ i18n, product, theme }),
                default: () => orCondition(
                    [isDifferentPayer, () => null],
                    [true, payment?.nextBilling?.lackReason === 'TooFarAhead'
                    && renderLackNextPaymentInformation({ formatMessage, formatPeriod }, theme, previewPeriod)]
                ),
            },
        },
        [EXPIRED]: {
            [EVERGREEN]: {
                default: () => EmptyInfoSection,
            },
            [TERMED]: {
                default: () => EmptyInfoSection,
            },
        },
        [EMPTY]: {
            default: () => EmptyInfoSection,
        },
    };

    const [subscriptionStatus, subscriptionType, renewalStatus = 'default'] = status.split('_');

    const path = [subscriptionStatus, subscriptionType, renewalStatus].filter(Boolean);
    const renderer = get(infoSectionRenderers, path, () => null)();

    return renderer ? renderer({ payment, subscriptionId, product, theme, action }) : <Divider theme={ theme } />;
};

InfoSection.propTypes = {
    status: statusPropType.isRequired,
    product: productPropType.isRequired,
    action: actionPropType,
    payment: paymentPropType.isRequired,
    publication: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string,
    theme: PropTypes.shape({}),
    i18n: i18nPropTypes,
};

export default InfoSection;
